import { useMemo } from "react";
import "dayjs/locale/ar-sa";
import "dayjs/locale/am";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Button,
  DialogContent,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { subYears } from "date-fns";

import { FormikTextField } from "../../components/Layout/FormikTextField";
import Toast from "../../components/Layout/Toast";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAuth } from "../auth/authSlice";
import { selectPreference } from "../preferences/preferencesSlice";
import { createEmployee, selectProcesses } from "../process/processSlices";
import { createEmployeeSchema } from "../process/validation";
import { MedicalFields } from "../process/components/MedicalFields";
import { Company } from "../setups/types/setupTypes";
import {
  countryFromOffice,
  foreignAgentPartnersFromSource,
  officePartnerSourceFromContext,
  resolveOfficeFromUserPartners,
  resolveOwnerForForeignOffice,
  resolveParentCompanyForOffice,
} from "../process/utils/officePartners";
import type { OfficePartnerContext } from "./types/officePartnerContext";
import {
  Employee,
  EmployeeStatus,
  GulfCountries,
  MedicalStatus,
  Nationalities,
  Sex,
} from "../process/types/processTypes";

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Grid item xs={12}>
    <Typography
      variant="caption"
      component="div"
      sx={{
        fontWeight: 700,
        color: "primary.main",
        letterSpacing: 1.2,
        textTransform: "uppercase",
        mt: 1,
        mb: -0.5,
      }}
    >
      {children}
    </Typography>
  </Grid>
);

interface Props {
  currentEmployee?: Employee | null;
  handleDialogClose: (saved?: boolean) => void;
  officeContext?: OfficePartnerContext | null;
  officeContextLoading?: boolean;
}

const DEFAULT_SEX = Sex.female;
const DEFAULT_MEDICAL = MedicalStatus.not_done;
const DEFAULT_NATIONALITY = Nationalities.ethiopian;

export const CallingForm = ({
  currentEmployee,
  handleDialogClose,
  officeContext,
  officeContextLoading = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { t: te } = useTranslation("enums");
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const { loading, error: saveFailed } = useAppSelector(selectProcesses);
  const { locale } = useAppSelector(selectPreference);

  const partnerSource = useMemo(
    () => officePartnerSourceFromContext(officeContext, user ?? undefined),
    [officeContext, user],
  );

  const partners = useMemo(
    () => foreignAgentPartnersFromSource(partnerSource),
    [partnerSource],
  );

  const initialValues = useMemo<Employee>(() => {
    const defaultPartner = partners[0];
    const defaultCountry = countryFromOffice(defaultPartner);

    if (currentEmployee?._id) {
      const resolvedPartner =
        resolveOfficeFromUserPartners(
          currentEmployee.partner,
          partnerSource,
        ) ?? defaultPartner;

      return {
        ...currentEmployee,
        calling: true,
        status: currentEmployee.status ?? EmployeeStatus.selected,
        sex: currentEmployee.sex ?? DEFAULT_SEX,
        medicalStatus: currentEmployee.medicalStatus ?? DEFAULT_MEDICAL,
        dateOfBirth: currentEmployee.dateOfBirth
          ? new Date(currentEmployee.dateOfBirth)
          : subYears(new Date(), 25),
        partner: resolvedPartner,
        address: {
          ...currentEmployee.address,
          telephone: currentEmployee.address?.telephone ?? "",
        },
        contactPerson: {
          ...currentEmployee.contactPerson,
          displayName: currentEmployee.contactPerson?.displayName ?? "",
          address: {
            ...currentEmployee.contactPerson?.address,
            telephone:
              currentEmployee.contactPerson?.address?.telephone ?? "",
          },
        },
        additionalData: {
          ...currentEmployee.additionalData,
          nationality:
            currentEmployee.additionalData?.nationality ?? DEFAULT_NATIONALITY,
          appliedCountry: countryFromOffice(resolvedPartner),
        },
      } as Employee;
    }

    return {
      status: EmployeeStatus.selected,
      calling: true,
      sex: DEFAULT_SEX,
      medicalStatus: DEFAULT_MEDICAL,
      dateOfBirth: subYears(new Date(), 25),
      givenName: "",
      surName: "",
      passportNumber: "",
      partner: defaultPartner,
      address: { telephone: "" },
      contactPerson: {
        displayName: "",
        address: { telephone: "" },
      },
      additionalData: {
        nationality: DEFAULT_NATIONALITY,
        appliedCountry: defaultCountry,
      },
    } as Employee;
  }, [currentEmployee, partners, partnerSource]);

  return (
    <div dir={i18n.dir()}>
      <DialogContent sx={{ px: 2, py: 1.5, pt: 2 }}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={createEmployeeSchema}
          onSubmit={async (values, actions) => {
            actions.setSubmitting(false);
            const office = resolveOfficeFromUserPartners(
              values.partner,
              partnerSource,
            );
            const owner =
              resolveOwnerForForeignOffice(office, partnerSource) ??
              user?.company;
            const parentCompany = resolveParentCompanyForOffice(
              owner,
              partnerSource,
            );
            let payload: Employee = {
              ...values,
              _id: values._id ?? currentEmployee?._id,
              calling: true,
              partner: office ?? values.partner,
              sex: values.sex ?? DEFAULT_SEX,
              medicalStatus: values.medicalStatus ?? DEFAULT_MEDICAL,
              additionalData: {
                ...values.additionalData,
                nationality:
                  values.additionalData?.nationality ?? DEFAULT_NATIONALITY,
                appliedCountry: countryFromOffice(office),
              },
            };
            if (owner) {
              payload = { ...payload, owner };
            }
            if (parentCompany) {
              payload = { ...payload, parentCompany };
            }
            const result = await dispatch(createEmployee(payload));
            if (createEmployee.fulfilled.match(result)) {
              handleDialogClose(true);
            }
          }}
        >
          {(props) => {
            const partnerId =
              props.values.partner?._id ?? partners[0]?._id ?? "";
            const appliedCountry = countryFromOffice(
              resolveOfficeFromUserPartners(
                props.values.partner,
                partnerSource,
              ),
            );

            const syncOffice = (office?: Company) => {
              const resolved = resolveOfficeFromUserPartners(
                office,
                partnerSource,
              );
              props.setFieldValue("partner", resolved);
              props.setFieldValue("additionalData", {
                ...props.values.additionalData,
                nationality:
                  props.values.additionalData?.nationality ??
                  DEFAULT_NATIONALITY,
                appliedCountry: countryFromOffice(resolved),
              });
            };

            return (
              <Form>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={te("For") + " " + te("country")}
                      value={appliedCountry}
                      disabled={Boolean(props.values.partner)}
                      onChange={(e) => {
                        props.setFieldValue("additionalData", {
                          ...props.values.additionalData,
                          nationality:
                            props.values.additionalData?.nationality ??
                            DEFAULT_NATIONALITY,
                          appliedCountry: e.target.value,
                        });
                      }}
                    >
                      {Object.keys(GulfCountries).map((option) => (
                        <MenuItem key={option} value={option}>
                          {te(option)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={t("calling.office")}
                      value={partnerId}
                      disabled={officeContextLoading || partners.length === 0}
                      onChange={(e) => {
                        const office = partners.find(
                          (c) => String(c._id) === e.target.value,
                        );
                        syncOffice(office);
                      }}
                    >
                      {partners.map((c) => (
                        <MenuItem key={c._id} value={c._id}>
                          {c.displayName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <SectionLabel>{t("calling.identity")}</SectionLabel>
                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      formikKey="givenName"
                      label={t("givenName")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormikTextField formikKey="surName" label={t("surName")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      formikKey="passportNumber"
                      label={t("passportNumber")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormikTextField formikKey="sex" select label={te("sex")}>
                      {Object.keys(Sex).map((option) => (
                        <MenuItem key={option} value={option}>
                          {te(option)}
                        </MenuItem>
                      ))}
                    </FormikTextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale={locale}
                    >
                      <DatePicker
                        label={t("dateOfBirth")}
                        views={["year", "month", "day"]}
                        minDate={new Date("1920-01-01")}
                        disableMaskedInput
                        inputFormat="DD-MMM-YYYY"
                        value={props.values.dateOfBirth}
                        onChange={(value) =>
                          props.setFieldValue("dateOfBirth", value, true)
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth size="small" />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <SectionLabel>{t("contact")}</SectionLabel>
                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      formikKey="address.telephone"
                      label={t("employeeTelephone")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      formikKey="contactPerson.displayName"
                      label={t("calling.contactName")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      formikKey="contactPerson.address.telephone"
                      label={t("contactTelephone")}
                    />
                  </Grid>

                  <SectionLabel>{t("calling.medical")}</SectionLabel>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        bgcolor: "action.hover",
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <MedicalFields
                        compact
                        value={{
                          medicalStatus:
                            props.values.medicalStatus ?? DEFAULT_MEDICAL,
                          medicalBookedDate: props.values.medicalBookedDate,
                          medicalExpiryDate: props.values.medicalExpiryDate,
                        }}
                        onChange={(patch) => {
                          Object.entries(patch).forEach(([key, val]) =>
                            props.setFieldValue(key, val, false),
                          );
                        }}
                        locale={locale}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  spacing={1}
                  sx={{
                    mt: 2,
                    pt: 1.5,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Button
                    size="small"
                    color="secondary"
                    variant="outlined"
                    onClick={() => handleDialogClose(false)}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    type="submit"
                    size="small"
                    color="secondary"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={
                      !props.isValid ||
                      loading === "pending" ||
                      officeContextLoading
                    }
                  >
                    {t("save")}
                  </Button>
                </Stack>
                {saveFailed?.message && (
                  <Toast severity="error">{saveFailed.message}</Toast>
                )}
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </div>
  );
};
